import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import "./ProductList.scss";
import { IProduct, IProductImage, IVariant } from "pages/guest/models/product";
import { IOnlineStoreState } from "redux/reducers/customer/onlineStore";
import { IOnlineStoreActions } from "redux/actions/customer/onlineStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import RangeSlider from "components/rangeSlider/RangeSlider";
import { ceil, groupBy, uniqBy } from "lodash";
import Spin from "components/spin/spin";
import { StatusCode } from "api/protocols";
import { GetGroupProducts, GetProductGroups } from "api/rpc/guest/groups";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { IProductGroup, IProductGroupProduct } from "pages/guest/models/groups";
import axios, { CancelToken } from "axios";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";

interface IProductListProps {
  onlineStoreStore: IOnlineStoreState;
  onlineStoreActions: IOnlineStoreActions;
  products: IProduct[];
  facilityShortName: string;
  facilityId: number;
  groupHandle: string;
  setGroupHandle: (groupHandle: string) => void;
  paramStartPrice: number;
  setParamStartPrice: (startPrice: number) => void;
  paramEndPrice: number;
  setParamEndPrice: (endPrice: number) => void;
  productRangeMaximumPrice: number;
  setProductRangeMaximumPrice: (updatedProductRangeMaximumPrice: number) => void;
}

interface IProductGroupWithProducts extends IProductGroup {
  products: IProductGroupProduct[];
}

type TProductGroupLeaf = {
  type: "leaf";
  products: IProductGroupProduct[];
};

type TProductGroupInternal = {
  type: "internal";
  subGroups: TProductSubGroupNode[];
  uniqueSubGroupProducts: IProductGroupProduct[];
};

type TProductGroupNode = {
  id: number;
  title: string;
  handle: string;
} & (TProductGroupLeaf | TProductGroupInternal);

type TProductSubGroupNode = {
  id: number;
  title: string;
  handle: string;
  products: IProductGroupProduct[];
};

type TProductGroupTree = TProductGroupNode[];

type TFilteredProduct = {
  id: number;
  handle: string;
  title: string;
  images: IProductImage[];
  startingProductPrice: number;
};

interface IProductListState {
  showCategories: boolean;
  showPriceRange: boolean;
  startPrice: number;
  endPrice: number;
  productGroupTree: TProductGroupTree;
  loadingProductGroups: boolean;
}

export const PRODUCT_RANGE_MINIMUM_PRICE = 0;
export const DEFAULT_PRODUCT_RANGE_MAXIMUM_PRICE = 2000;

export default function ProductList(props: IProductListProps) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const windowSize = useWindowSize();

  const dirtyEndPrice = useRef<boolean>(false);

  const [state, setState] = useState<IProductListState>({
    showCategories: false,
    showPriceRange: false,
    startPrice: props.paramStartPrice,
    endPrice: props.paramEndPrice,
    productGroupTree: [],
    loadingProductGroups: true,
  });

  function getVariantPriceOverride(variant: IVariant) {
    if (!variant) {
      return null;
    }

    const priceOverride = variant.inventory_item?.inventory_levels?.find(
      inventoryLevel => inventoryLevel?.facility_id === props.facilityId,
    )?.price_override;

    return priceOverride ?? variant.price;
  }

  function getStartingProductPrice(product: IProduct) {
    const variants = product?.variants;

    if (variants == null || variants.length === 0) {
      return null;
    }

    const variantPrices = variants.map(getVariantPriceOverride).filter(price => price != null);

    if (variantPrices.length > 0) {
      const lowestPrice = variantPrices.reduce((previousPrice, currentPrice) =>
        previousPrice < currentPrice ? previousPrice : currentPrice,
      );

      return lowestPrice;
    } else {
      return null;
    }
  }

  function productToFilteredProduct(product: IProduct): TFilteredProduct {
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      images: product.images,
      startingProductPrice: getStartingProductPrice(product),
    };
  }

  function productMatchesSearchQuery(product: IProduct) {
    const upperCasedSearchQuery = props.onlineStoreStore.productSearchQuery.toUpperCase();
    const upperCasedProductTitle = product.title.toUpperCase();
    return upperCasedProductTitle.indexOf(upperCasedSearchQuery) > -1;
  }

  const filteredProducts: TFilteredProduct[] = useMemo(() => {
    if (props.onlineStoreStore.productSearchQuery?.length > 0) {
      return props.products.filter(productMatchesSearchQuery).map(productToFilteredProduct);
    }

    let groupProducts: IProductGroupProduct[] = null;

    for (let i = 0; i < state.productGroupTree.length && groupProducts == null; i++) {
      const node = state.productGroupTree[i];

      if (node.handle === props.groupHandle) {
        if (node.type === "internal") {
          groupProducts = node.uniqueSubGroupProducts;
        } else if (node.type === "leaf") {
          groupProducts = node.products;
        }
      } else if (node.type === "internal") {
        const selectedSubGroup = node.subGroups.find(subGroup => subGroup.handle === props.groupHandle);

        if (selectedSubGroup) {
          groupProducts = selectedSubGroup.products;
        }
      }
    }

    if (groupProducts?.length > 0) {
      const filteredProducts: TFilteredProduct[] = groupProducts
        .reduce((accumulator: IProduct[], groupProduct: IProductGroupProduct) => {
          const matchingProduct = props.products.find(product => product.id === groupProduct.id);

          if (matchingProduct) {
            return [...accumulator, matchingProduct];
          } else {
            return accumulator;
          }
        }, [])
        .map(productToFilteredProduct)
        .filter(product => {
          return (
            product.startingProductPrice != null &&
            product.startingProductPrice >= props.paramStartPrice &&
            product.startingProductPrice <= props.paramEndPrice
          );
        });

      return filteredProducts;
    } else {
      return [];
    }
  }, [
    props.products,
    props.onlineStoreStore.productSearchQuery,
    state.productGroupTree,
    props.groupHandle,
    props.paramStartPrice,
    props.paramEndPrice,
  ]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (state.loadingProductGroups && windowSize.width != null) {
      const greaterThanMobileSize = windowSize.width > MOBILE_WIDTH;

      setState(prev => ({
        ...prev,
        showCategories: greaterThanMobileSize,
        showPriceRange: greaterThanMobileSize,
      }));

      void loadProductGroups(source.token);
    }

    return () => source.cancel();
  }, [state.loadingProductGroups, windowSize]);

  function getUniqueSubGroupProducts(subGroups: TProductSubGroupNode[]) {
    const subGroupProducts: IProductGroupProduct[] = [];

    subGroups.forEach(subGroup => {
      subGroupProducts.push(...subGroup.products);
    });

    return uniqBy(subGroupProducts, subGroupProduct => subGroupProduct.id);
  }

  function compareGroupProductByPosition(prev: IProductGroupProduct, next: IProductGroupProduct) {
    return prev.position - next.position;
  }

  async function loadProductGroups(token: CancelToken) {
    try {
      if (props.products == null || props.products.length === 0) {
        return;
      }

      const getProductGroupsResponse = await GetProductGroups({ facility_id: props.facilityId }, false, token);

      if (token && token.reason) {
        return;
      }

      if (getProductGroupsResponse.status !== StatusCode.OK) {
        dispatch(showError("Failed to load product groups")); // TODO: Translation
        return;
      }

      const productGroups = getProductGroupsResponse.data;

      const productGroupsWithProducts: IProductGroupWithProducts[] = productGroups.map(productGroup => ({
        ...productGroup,
        products: [],
      }));

      const getGroupProductsResponse = await GetGroupProducts(
        { facility_id: props.facilityId, group_id: productGroupsWithProducts.map(productGroup => productGroup.id) },
        false,
        token,
      );

      if (token && token.reason) {
        return;
      }

      if (getGroupProductsResponse.status !== StatusCode.OK) {
        dispatch(showError("Failed to load products in groups")); // TODO: Translation
        return;
      }

      const groupProducts = getGroupProductsResponse.data;

      productGroupsWithProducts.forEach(productGroupWithProducts => {
        const productGroupProducts = groupProducts.filter(
          groupProduct => groupProduct.product_group_id === productGroupWithProducts.id,
        );

        const filteredProducts = productGroupProducts.filter(productGroupProduct => {
          return props.products.some(product => {
            return product.id === productGroupProduct.id;
          });
        });

        productGroupWithProducts.products.push(...filteredProducts);
      });

      const groupTypeProductGroups = productGroupsWithProducts.filter(productGroup => productGroup.type === "group");
      const subGroupTypeProductGroups = productGroupsWithProducts.filter(
        productGroup => productGroup.type === "sub_group",
      );

      const productGroupTree: TProductGroupTree = groupTypeProductGroups.map(groupTypeProductGroup => {
        const productGroupNode: TProductGroupNode = {
          id: groupTypeProductGroup.id,
          title: groupTypeProductGroup.title,
          handle: groupTypeProductGroup.handle,
          type: "leaf",
          products: groupTypeProductGroup.products.sort(compareGroupProductByPosition),
        };

        return productGroupNode;
      });

      const parentIdGroupedSubGroups = groupBy(subGroupTypeProductGroups, productGroup => productGroup.parent_id);

      for (const parentId in parentIdGroupedSubGroups) {
        const parentGroupIndex = productGroupTree.findIndex(
          productGroupNode => productGroupNode.id === Number(parentId),
        );

        if (parentGroupIndex !== -1) {
          const subGroupNodes: TProductSubGroupNode[] = parentIdGroupedSubGroups[parentId].map(subGroup => ({
            id: subGroup.id,
            title: subGroup.title,
            handle: subGroup.handle,
            products: subGroup.products.sort(compareGroupProductByPosition),
          }));

          productGroupTree[parentGroupIndex] = {
            ...productGroupTree[parentGroupIndex],
            type: "internal",
            subGroups: subGroupNodes,
            uniqueSubGroupProducts: getUniqueSubGroupProducts(subGroupNodes),
          };
        }
      }

      const allProductsId = 0;
      const allProductsHandle = "all_products";

      productGroupTree.unshift({
        id: allProductsId,
        title: "All Products", // TODO: Translation
        handle: allProductsHandle,
        type: "leaf",
        products: props.products.map(product => ({
          id: product.id,
          product_group_id: allProductsId,
          subtitle: product.subtitle,
          title: product.title,
          type: product.type,
          position: 0,
        })),
      });

      const productRangeMaximumPrice = props.products
        .map(product => getStartingProductPrice(product))
        .reduce((prev, next) => (prev > next ? prev : next), props.productRangeMaximumPrice);

      const productRangeMaximumPriceNearest100 = ceil(productRangeMaximumPrice, -2);

      if (
        props.productRangeMaximumPrice < productRangeMaximumPriceNearest100 &&
        !dirtyEndPrice.current &&
        state.endPrice === DEFAULT_PRODUCT_RANGE_MAXIMUM_PRICE
      ) {
        props.setParamEndPrice(productRangeMaximumPriceNearest100);
        setState(prev => ({
          ...prev,
          endPrice: productRangeMaximumPriceNearest100,
        }));
      }

      props.setProductRangeMaximumPrice(productRangeMaximumPriceNearest100);

      setState(prev => ({
        ...prev,
        productGroupTree,
      }));

      if (props.groupHandle === "") {
        props.setGroupHandle(allProductsHandle);
      }
    } catch {
      dispatch(showError("Error loading product groups")); // TODO: Translation
    } finally {
      setState(prev => ({ ...prev, loadingProductGroups: false }));
    }
  }

  function navigateToSingleProduct(productHandle: string) {
    props.onlineStoreActions.updateProductSearchQuery("");
    history.push(`/online-store/${props.facilityShortName}/product/${productHandle}`);
  }

  function handleClickGroup(handle: string) {
    if (props.groupHandle === handle) {
      const parentGroupHandle = state.productGroupTree.find(
        node => node.type === "internal" && !!node.subGroups.find(subGroup => subGroup.handle === handle),
      )?.handle;

      if (parentGroupHandle != null) {
        props.setGroupHandle(parentGroupHandle);
      } else {
        props.setGroupHandle("");
      }
    } else {
      props.setGroupHandle(handle);
    }
  }

  const startPriceDebounceTimer = useRef<NodeJS.Timeout>(null);

  function setStartPrice(updatedStartPrice: number) {
    setState(prev => ({ ...prev, startPrice: updatedStartPrice }));

    if (startPriceDebounceTimer.current != null) {
      clearTimeout(startPriceDebounceTimer.current);
    }

    startPriceDebounceTimer.current = setTimeout(() => {
      props.setParamStartPrice(updatedStartPrice);
      startPriceDebounceTimer.current = null;
    }, 200);
  }

  const endPriceDebounceTimer = useRef<NodeJS.Timeout>(null);

  function setEndPrice(updatedEndPrice: number) {
    dirtyEndPrice.current = true;

    setState(prev => ({ ...prev, endPrice: updatedEndPrice }));

    if (endPriceDebounceTimer.current != null) {
      clearTimeout(endPriceDebounceTimer.current);
    }

    endPriceDebounceTimer.current = setTimeout(() => {
      props.setParamEndPrice(updatedEndPrice);
      endPriceDebounceTimer.current = null;
    }, 200);
  }

  function calculateGroupNodeProductQuantity(node: TProductGroupNode) {
    if (node.type === "internal") {
      const uniqueSubGroupProducts = node.uniqueSubGroupProducts;
      return uniqueSubGroupProducts?.length ?? 0;
    } else if (node.type === "leaf") {
      return node.products.length ?? 0;
    } else {
      return 0;
    }
  }

  return (
    <div className="product-list-container">
      <div className="product-list-filter-section">
        <div>
          <div
            className="product-list-accordian"
            onClick={() => setState(prev => ({ ...prev, showCategories: !prev.showCategories }))}
          >
            <div>Categories</div> {/* TODO: Translation */}
            <FontAwesomeIcon
              className="product-list-accordian-chevron"
              icon={state.showCategories ? faChevronUp : faChevronDown}
            />
          </div>
          {state.showCategories && (
            <>
              {state.loadingProductGroups && windowSize.width > MOBILE_WIDTH && (
                <div id="product-list-loading-product-groups-spin-container">
                  <Spin className="product-list-loading-product-groups-spin" />
                </div>
              )}
              <div className="product-list-groups">
                {state.productGroupTree.map(productGroupNode => {
                  const isGroupSelected = props.groupHandle === productGroupNode.handle;
                  let selectedSubGroup: TProductSubGroupNode = undefined;

                  if (productGroupNode.type === "internal") {
                    selectedSubGroup = productGroupNode.subGroups.find(
                      subGroup => subGroup.handle === props.groupHandle,
                    );
                  }

                  return (
                    <div key={productGroupNode.id}>
                      <div
                        className="product-list-group"
                        style={{ fontWeight: isGroupSelected && "700" }}
                        onClick={() => handleClickGroup(productGroupNode.handle)}
                      >
                        {productGroupNode.title}{" "}
                      </div>
                      {(isGroupSelected || selectedSubGroup != null) && productGroupNode.type === "internal" && (
                        <div className="product-list-subgroups">
                          {productGroupNode.subGroups.map(subGroup => {
                            return (
                              <div
                                key={subGroup.id}
                                className="product-list-group"
                                style={{ fontWeight: selectedSubGroup?.handle === subGroup.handle && "700" }}
                                onClick={() => handleClickGroup(subGroup.handle)}
                              >
                                {subGroup.title}{" "}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div>
          <div
            className="product-list-accordian"
            onClick={() => setState(prev => ({ ...prev, showPriceRange: !prev.showPriceRange }))}
          >
            <div>Price</div> {/* TODO: Translation */}
            <FontAwesomeIcon
              className="product-list-accordian-chevron"
              icon={state.showPriceRange ? faChevronUp : faChevronDown}
            />
          </div>
          {state.showPriceRange && (
            <div className="product-list-price-range">
              <RangeSlider
                range={{
                  type: "dual",
                  start: state.startPrice,
                  startLabel: `C$${state.startPrice}`,
                  end: state.endPrice,
                  endLabel: `C$${state.endPrice}`,
                  setStart: setStartPrice,
                  setEnd: setEndPrice,
                }}
                min={PRODUCT_RANGE_MINIMUM_PRICE}
                max={props.productRangeMaximumPrice}
                step={1}
              />
            </div>
          )}
        </div>
      </div>
      <div className="product-list">
        {state.loadingProductGroups && windowSize.width <= MOBILE_WIDTH && (
          <div id="product-list-loading-product-groups-spin-container">
            <Spin className="product-list-loading-product-groups-spin" />
          </div>
        )}
        {filteredProducts.map(product => {
          return (
            <div key={product.id}>
              <img
                className="product-image"
                src={product.images?.[0]?.source}
                onClick={() => navigateToSingleProduct(product.handle)}
              />
              <div className="product-information">
                <div>{product.title}</div>
                <div>
                  <div className="product-information-starting-at">Starting at</div> {/* TODO: Translation */}
                  <div>
                    {product.startingProductPrice != null ? `C$${product.startingProductPrice.toFixed(2)}` : ""}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
